import * as Commands from "../model/commands"
import {
  type ID,
  type Idea,
  ideaHeight,
  ideaWidth,
  type Location,
  type LocationDiff,
} from "../model/types"
import { PlaneOffset, PlateView } from "../plate_viewer"

class DragIdeas {
  draggingIdeaIds: ID[]
  shouldDragSelection: boolean
  plateView: PlateView

  constructor(draggingIdeaIds: ID[], shouldDragSelection: boolean, plateView: PlateView) {
    this.draggingIdeaIds = draggingIdeaIds
    this.shouldDragSelection = shouldDragSelection
    this.plateView = plateView
    this.plateView.renderer.set_ideas_cursor("grabbing")
    this.plateView.renderer.render_drag_preview(this.dragging_ideas(), this.plateView.scale)
    this.plateView.renderer.move_drag_preview({ dpx: 0, dpy: 0 })
    this.plateView.hideIdeas(this.draggingIdeaIds)
  }

  showMovingWithCursor(planeOffset: PlaneOffset): void {
    this.plateView.renderer.move_drag_preview(planeOffset)
    if (this.shouldDragSelection) this.plateView.renderIdeaSelection(false, planeOffset)
  }

  place(diff: LocationDiff): void {
    if (this.draggingIdeaIds.length) {
      this.moveIdeas(this.draggingIdeaIds, diff)
      this.plateView.showIdeas(this.draggingIdeaIds)
      this.plateView.renderer.move_drag_preview({ dpx: 0, dpy: 0 })
      this.plateView.renderIdeaSelection(false, { dpx: 0, dpy: 0 })
    }
    this.plateView.renderer.clear_drag_preview()
  }

  removeIdeaAt(location: Location): void {
    this.draggingIdeaIds = this.draggingIdeaIds.filter((id) => {
      const idea = this.plateView.model.getIdea(id)

      return idea != null && !(idea.location.gx === location.gx && idea.location.gy === location.gy)
    })
  }

  abort(): void {
    this.plateView.showIdeas(this.draggingIdeaIds)
    this.draggingIdeaIds = []
    this.plateView.renderer.clear_drag_preview()
  }

  moveIdeas(ideaIds: ID[], diff: LocationDiff) {
    const { dx: dgx, dy: dgy } = diff

    const canMove = ideaIds.every((id) => {
      const idea = this.plateView.model.fetchIdea(id)
      // verify none of the moved ideas will go on top of an existing idea
      let existingAtThisLocation = false
      for (let i = 0; i < ideaWidth(idea); i++) {
        for (let j = 0; j < ideaHeight(idea); j++) {
          const ideaHere = this.plateView.model.getIdeaAt({
            gx: idea.location.gx + dgx + i,
            gy: idea.location.gy + dgy + j,
          })
          if (ideaHere != null && !ideaIds.includes(ideaHere.id)) {
            existingAtThisLocation = true
          }
        }
      }
      return !existingAtThisLocation
    })

    if (!canMove) return false

    if (!(dgx === 0 && dgy === 0))
      this.plateView.commander.exec(
        new Commands.MoveIdeasCommand({
          ideaLocations: ideaIds.map((id) => {
            const idea = this.plateView.model.fetchIdea(id)
            return [id, { gx: idea.location.gx + dgx, gy: idea.location.gy + dgy }]
          }),
        }),
      )
    this.plateView.renderIdeaSelection(false)
  }

  private dragging_ideas(): Idea[] {
    return this.draggingIdeaIds.map((id) => this.plateView.model.fetchIdea(id))
  }
}

export default DragIdeas
